import React, { useMemo, useState } from 'react';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import FormLabel from '@material-ui/core/FormLabel';
import Radio from '@material-ui/core/Radio';
import MuiRadioGroup from '@material-ui/core/RadioGroup';
import styled from 'styled-components';
import { ChipsSelect, tokens, utmostTheme } from '@utmost.co/utmost-design-system';
import { ComponentPropBase } from '../../types';
import { objectUtil } from '../../forms/utils/objectUtil';
import { useSmartFormContext } from '../../forms/utils/useSmartFormContext';
import { Button } from '../../atoms/Button';
import { InfoText } from '../../atoms/InfoText/InfoText';
import { useField } from 'formik';
import { useInitialValueMapper } from '../utils/useInitialValueMapper';
import { normalizeString } from '../utils/normalizeString';

interface ValueModel<T> {
  label: string;
  value: T;
}

export interface RadioGroupAtomProps<OptionValue, TargetModel extends any = OptionValue> extends ComponentPropBase {
  label: string;
  name: string;
  values: ValueModel<OptionValue>[];
  mapInputValue?: (value?: TargetModel) => OptionValue | undefined;
  mapOutputValue?: (value?: OptionValue) => TargetModel | undefined | null;
  onSetFieldValue?: (model: TargetModel | undefined | null) => void;
  initialValue?: string | undefined;
  view?: 'radio' | 'chip' | 'button';
}

export function RadioGroupAtom<T, TargetModel extends any = T>({
  label,
  name,
  values,
  mapOutputValue,
  mapInputValue,
  errors,
  helpText,
  infoText,
  touched,
  disabled,
  required,
  view,
  onSetFieldValue,
  initialValue,
  ...rest
}: RadioGroupAtomProps<T, TargetModel>) {
  const { values: formValues, readonly, getDisabled, submitCount } = useSmartFormContext();
  const [, , helpers] = useField<TargetModel | undefined | null>(name || 'INVALID');
  const valueSetter = useMemo(() => onSetFieldValue ?? helpers.setValue, [onSetFieldValue, helpers.setValue]);
  const error = submitCount > 0 && !!errors;
  const [isFocused, setIsFocused] = useState(false);
  if (getDisabled(name) || readonly) {
    disabled = true;
  }
  const mapInput = mapInputValue || ((value: TargetModel) => value as unknown as T);
  const mapOutput = mapOutputValue || ((value: T) => value as unknown as TargetModel);
  const selectedValue = mapInput(objectUtil.getIn(formValues, name));

  useInitialValueMapper(
    !!initialValue ? getInitialValue(initialValue as string | undefined, values) : null,
    (initValue) => valueSetter(mapOutput(initValue))
  );

  function handleToggleField(value: T) {
    if (value !== selectedValue) {
      valueSetter(mapOutput(value));
    } else {
      valueSetter(undefined);
    }
  }

  return (
    <FormControl
      error={error}
      required={required}
      data-test={`${name}-field-control`}
      className="field-control"
      disabled={disabled}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      {...rest}
    >
      <StyledFormLabelStyled error={error} focused={isFocused}>
        <InfoText label={label} required={required} tooltipText={infoText} />
      </StyledFormLabelStyled>
      {helpText?.trim() && (
        <StyledFormHelperTextStyled $xxSmall={view === 'radio'}>{helpText}</StyledFormHelperTextStyled>
      )}
      {(!view || view === 'button') && (
        <StyledRadioButtons>
          {values.map(({ label, value }) => (
            <StyledRadioButtonStyled
              classes={{
                contained: 'selected'
              }}
              key={value?.toString()}
              data-test={`${name}-field-${value}`}
              onClick={() => handleToggleField(value)}
              variant={selectedValue === value ? 'contained' : 'outlined'}
              disabled={disabled}
            >
              {label}
            </StyledRadioButtonStyled>
          ))}
        </StyledRadioButtons>
      )}
      {view === 'radio' && (
        <MuiRadioGroup
          aria-label={name}
          name={name}
          value={typeof selectedValue === 'undefined' ? null : selectedValue}
        >
          {values.map(({ label, value }) => (
            <FormControlLabel
              key={`${name}-field-${value}`}
              value={value}
              onChange={() => handleToggleField(value)}
              control={<Radio color="primary" />}
              label={label}
              data-test={`${name}-field-${value}`}
              data-checked={selectedValue === value}
              disabled={disabled}
            />
          ))}
        </MuiRadioGroup>
      )}
      {view === 'chip' && (
        <StyledRadioButtons>
          <ChipsSelect<T, false>
            value={selectedValue}
            onSelect={(value) => value && handleToggleField(value)}
            dataTest={`${name}-field`}
            options={values}
          />
        </StyledRadioButtons>
      )}

      {error && <StyledFormErrorTextStyled error>{errors}</StyledFormErrorTextStyled>}
    </FormControl>
  );
}

const StyledFormLabelStyled = styled(FormLabel)`
  && {
    font-weight: 500;
  }
`;

const StyledFormHelperTextStyled = styled(FormHelperText)<{ $xxSmall?: boolean }>`
  && {
    font-size: ${({ $xxSmall }) => ($xxSmall ? utmostTheme.font.sizeXXSmall : utmostTheme.font.sizeSmall)};
  }
`;

const StyledFormErrorTextStyled = styled(FormHelperText)`
  && {
    font-size: ${utmostTheme.font.sizeXSmall};
  }
`;

const StyledRadioButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${utmostTheme.spacingSize.m};
  margin-top: ${utmostTheme.spacingSize.m};
`;

const StyledRadioButtonStyled = styled(Button)`
  && {
    text-transform: initial;
    transition: border 0ms ease 0ms; // Stop buttons moving on state changes.
  }
  &.selected,
  &.selected:hover {
    background-color: ${tokens.colorSuccess};
    color: white;
  }
`;

function getInitialValue<T>(value: string | undefined | null, values: { label: string; value: T }[]): T | undefined {
  if (!value) {
    return undefined;
  }
  const normalizedValue = normalizeString(value);
  return (
    values.find((it) => {
      const labelMatch = normalizeString(it.label) === normalizedValue;
      if (labelMatch) {
        return true;
      }
      if (typeof it.value === 'string') {
        return normalizeString(it.value) === normalizedValue;
      }
      return false;
    })?.value ?? undefined
  );
}
